import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Drawer,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  TablePagination,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import './EksMonitoringDashboard.css';

const API_BASE = process.env.REACT_APP_API_URL;

// TabPanel component remains as is since it's a functional component with simple styling
const TabPanel = ({ children, value, index }) => (
  <div className="tabPanel" hidden={value !== index}>
    {value === index && children}
  </div>
);

const EksMonitoringDashboard = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCluster, setSelectedCluster] = useState('');
  const [nodes, setNodes] = useState([]);
  const [pods, setPods] = useState([]);
  const [namespaces, setNamespaces] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCategory, setSearchCategory] = useState('all');
  const [searchResults, setSearchResults] = useState({
    nodes: [],
    pods: [],
    namespaces: [],
    deployments: []
  });

  // Logs state
  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [logs, setLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    // Get cluster name from query parameter
    const queryParams = new URLSearchParams(location.search);
    const clusterParam = queryParams.get('cluster');
    
    if (clusterParam) {
      setSelectedCluster(clusterParam);
    } else {
      // Get default cluster name from environment variable
      const defaultClusterName = process.env.REACT_APP_EKS_CLUSTER_NAME;
      setSelectedCluster(defaultClusterName);
    }
  }, [location, accountId]);

  useEffect(() => {
    if (selectedCluster) {
      fetchResourceData();
    }
  }, [selectedCluster, selectedTab]);

  // Effect to filter resources based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      // If search term is empty, show all data
      setSearchResults({
        nodes: nodes,
        pods: pods,
        namespaces: namespaces,
        deployments: deployments
      });
      return;
    }

    // Convert search term to lowercase for case-insensitive comparison
    const term = searchTerm.toLowerCase();

    // Filter nodes
    const filteredNodes = nodes.filter(node => {
      if (searchCategory !== 'all' && searchCategory !== 'nodes') return false;
      
      return (
        node.name.toLowerCase().includes(term) ||
        node.status.toLowerCase().includes(term) ||
        node.instanceType.toLowerCase().includes(term) ||
        node.osImage.toLowerCase().includes(term) ||
        node.kubeletVersion.toLowerCase().includes(term)
      );
    });

    // Filter pods
    const filteredPods = pods.filter(pod => {
      if (searchCategory !== 'all' && searchCategory !== 'pods') return false;
      
      return (
        pod.name.toLowerCase().includes(term) ||
        pod.namespace.toLowerCase().includes(term) ||
        pod.status.toLowerCase().includes(term) ||
        pod.nodeName.toLowerCase().includes(term) ||
        (pod.ip && pod.ip.toLowerCase().includes(term))
      );
    });

    // Filter namespaces
    const filteredNamespaces = namespaces.filter(ns => {
      if (searchCategory !== 'all' && searchCategory !== 'namespaces') return false;
      
      return (
        ns.name.toLowerCase().includes(term) ||
        ns.status.toLowerCase().includes(term)
      );
    });

    // Filter deployments
    const filteredDeployments = deployments.filter(deploy => {
      if (searchCategory !== 'all' && searchCategory !== 'deployments') return false;
      
      return (
        deploy.name.toLowerCase().includes(term) ||
        deploy.namespace.toLowerCase().includes(term) ||
        (deploy.strategy && deploy.strategy.toLowerCase().includes(term))
      );
    });

    // Update search results
    setSearchResults({
      nodes: filteredNodes,
      pods: filteredPods,
      namespaces: filteredNamespaces,
      deployments: filteredDeployments
    });

    // Auto-select the tab with results if current tab has no results
    if (searchTerm.length > 0) {
      const currentTabHasResults = (
        (selectedTab === 0 && filteredNodes.length > 0) ||
        (selectedTab === 1 && filteredPods.length > 0) ||
        (selectedTab === 2 && filteredNamespaces.length > 0) ||
        (selectedTab === 3 && filteredDeployments.length > 0)
      );

      if (!currentTabHasResults) {
        // Find first tab with results
        if (filteredNodes.length > 0) {
          setSelectedTab(0);
        } else if (filteredPods.length > 0) {
          setSelectedTab(1);
        } else if (filteredNamespaces.length > 0) {
          setSelectedTab(2);
        } else if (filteredDeployments.length > 0) {
          setSelectedTab(3);
        }
      }
    }
  }, [searchTerm, searchCategory, nodes, pods, namespaces, deployments]);

  // No need to fetch clusters as we're using the one from URL parameter

  const fetchResourceData = async () => {
    setLoading(true);
    try {
      let response;
      switch (selectedTab) {
        case 0: // Nodes
          response = await axios.get(`${API_BASE}/nodes/${accountId}/${selectedCluster}`);
          setNodes(response.data);
          break;
        case 1: // Pods
          response = await axios.get(`${API_BASE}/pods/${accountId}/${selectedCluster}`);
          setPods(response.data);
          break;
        case 2: // Namespaces
          response = await axios.get(`${API_BASE}/namespaces/${accountId}/${selectedCluster}`);
          setNamespaces(response.data);
          break;
        case 3: // Deployments
          response = await axios.get(`${API_BASE}/deployments/${accountId}/${selectedCluster}`);
          setDeployments(response.data);
          break;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Initialize empty arrays to prevent rendering errors
      if (selectedTab === 0) setNodes([]);
      if (selectedTab === 1) setPods([]);
      if (selectedTab === 2) setNamespaces([]);
      if (selectedTab === 3) setDeployments([]);
      
      // Display error details in console for debugging
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Error request:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error message:', error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setPage(0); // Reset to first page when changing tabs
  };
  
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClusterChange = (clusterName) => {
    setSelectedCluster(clusterName);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleSearchCategoryChange = (event) => {
    setSearchCategory(event.target.value);
  };

  const renderSearchResultsCount = () => {
    const { nodes: filteredNodes, pods: filteredPods, namespaces: filteredNamespaces, deployments: filteredDeployments } = searchResults;
    
    if (searchTerm.trim() === '') return null;
    
    const totalResults = filteredNodes.length + filteredPods.length + filteredNamespaces.length + filteredDeployments.length;
    
    return (
      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip 
          label={`Total: ${totalResults} results`} 
          color="primary" 
          variant="outlined" 
        />
        {filteredNodes.length > 0 && (
          <Chip 
            label={`Nodes: ${filteredNodes.length}`} 
            color="info" 
            variant="outlined" 
            onClick={() => setSelectedTab(0)}
          />
        )}
        {filteredPods.length > 0 && (
          <Chip 
            label={`Pods: ${filteredPods.length}`} 
            color="info" 
            variant="outlined"
            onClick={() => setSelectedTab(1)}
          />
        )}
        {filteredNamespaces.length > 0 && (
          <Chip 
            label={`Namespaces: ${filteredNamespaces.length}`} 
            color="info" 
            variant="outlined"
            onClick={() => setSelectedTab(2)}
          />
        )}
        {filteredDeployments.length > 0 && (
          <Chip 
            label={`Deployments: ${filteredDeployments.length}`} 
            color="info" 
            variant="outlined"
            onClick={() => setSelectedTab(3)}
          />
        )}
      </Box>
    );
  };

  const renderNodesTable = () => {
    const data = searchTerm ? searchResults.nodes : nodes;
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;
    
    return (
      <TableContainer component={Paper} className="tableContainer">
        <Table className="table">
          <TableHead className="tableHeader">
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Instance Type</TableCell>
              <TableCell>OS Image</TableCell>
              <TableCell>Kubelet Version</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((node) => (
                <TableRow key={node.name}>
                  <TableCell>
                    <Button 
                      variant="text" 
                      color="primary"
                      onClick={() => fetchPodLogs(node.name, 'default')}
                    >
                      {node.name}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={node.status} 
                      color={node.status === 'Ready' ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{node.instanceType}</TableCell>
                  <TableCell>{node.osImage}</TableCell>
                  <TableCell>{node.kubeletVersion}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No nodes found
                </TableCell>
              </TableRow>
            )}
            {emptyRows > 0 && (
              <TableRow style={{ height: 56 * emptyRows }}>
                <TableCell colSpan={5} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    );
  };

  const renderPodsTable = () => {
    const data = searchTerm ? searchResults.pods : pods;
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;
    
    return (
      <TableContainer component={Paper} className="tableContainer">
        <Table className="table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Namespace</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Node</TableCell>
              <TableCell>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((pod) => (
                <TableRow key={pod.name}>
                  <TableCell>
                    <Button 
                      variant="text" 
                      color="primary"
                      onClick={() => fetchPodLogs(pod.name, pod.namespace)}
                      sx={{ textAlign: 'left', justifyContent: 'flex-start' }}
                    >
                      {pod.name}
                    </Button>
                  </TableCell>
                  <TableCell>{pod.namespace}</TableCell>
                  <TableCell>{pod.status}</TableCell>
                  <TableCell>{pod.nodeName}</TableCell>
                  <TableCell>{pod.ip}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No pods found
                </TableCell>
              </TableRow>
            )}
            {emptyRows > 0 && (
              <TableRow style={{ height: 56 * emptyRows }}>
                <TableCell colSpan={5} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    );
  };

  const renderNamespacesTable = () => {
    const data = searchTerm ? searchResults.namespaces : namespaces;
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;
    
    return (
      <TableContainer component={Paper} className="tableContainer">
        <Table className="table">
          <TableHead className="tableHeader">
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((ns) => (
                <TableRow key={ns.name}>
                  <TableCell>{ns.name}</TableCell>
                  <TableCell>{ns.status}</TableCell>
                  <TableCell>{new Date(ns.creationTimestamp).toLocaleString()}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No namespaces found
                </TableCell>
              </TableRow>
            )}
            {emptyRows > 0 && (
              <TableRow style={{ height: 56 * emptyRows }}>
                <TableCell colSpan={3} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    );
  };

  const renderDeploymentsTable = () => {
    const data = searchTerm ? searchResults.deployments : deployments;
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;
    
    return (
      <TableContainer component={Paper} className="tableContainer">
        <Table className="table">
          <TableHead className="tableHeader">
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Namespace</TableCell>
              <TableCell>Replicas</TableCell>
              <TableCell>Available</TableCell>
              <TableCell>Strategy</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((deploy) => (
                <TableRow key={deploy.name}>
                  <TableCell>
                    <Button 
                      variant="text" 
                      color="primary"
                      onClick={() => fetchDeploymentLogs(deploy.name, deploy.namespace)}
                      sx={{ textAlign: 'left', justifyContent: 'flex-start' }}
                    >
                      {deploy.name}
                    </Button>
                  </TableCell>
                  <TableCell>{deploy.namespace}</TableCell>
                  <TableCell>{deploy.replicas.desired}</TableCell>
                  <TableCell>{deploy.replicas.available}/{deploy.replicas.ready}</TableCell>
                  <TableCell>{deploy.strategy}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No deployments found
                </TableCell>
              </TableRow>
            )}
            {emptyRows > 0 && (
              <TableRow style={{ height: 56 * emptyRows }}>
                <TableCell colSpan={5} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    );
  };

  // Function to fetch logs for a pod
  const fetchPodLogs = async (podName, namespace) => {
    setLoadingLogs(true);
    try {
      const response = await axios.get(
        `${API_BASE}/logs/${accountId}/${selectedCluster}/${namespace}/${podName}`
      );
      setLogs(response.data.logs);
      setSelectedResource({
        type: 'pod',
        name: podName,
        namespace: namespace
      });
      setLogsDrawerOpen(true);
    } catch (error) {
      console.error('Error fetching pod logs:', error);
      setLogs(['Error fetching logs: ' + error.message]);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Function to fetch logs for a deployment
  const fetchDeploymentLogs = async (deploymentName, namespace) => {
    setLoadingLogs(true);
    try {
      const response = await axios.get(
        `${API_BASE}/deployment-logs/${accountId}/${selectedCluster}/${namespace}/${deploymentName}`
      );
      setLogs(response.data.logs);
      setSelectedResource({
        type: 'deployment',
        name: deploymentName,
        namespace: namespace
      });
      setLogsDrawerOpen(true);
    } catch (error) {
      console.error('Error fetching deployment logs:', error);
      setLogs(['Error fetching logs: ' + error.message]);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Function to refresh logs
  const refreshLogs = () => {
    if (!selectedResource) return;
    
    if (selectedResource.type === 'pod') {
      fetchPodLogs(selectedResource.name, selectedResource.namespace);
    } else if (selectedResource.type === 'deployment') {
      fetchDeploymentLogs(selectedResource.name, selectedResource.namespace);
    }
  };

  // Render logs sidebar
  const renderLogsDrawer = () => (
    <Drawer
      anchor="right"
      open={logsDrawerOpen}
      onClose={() => setLogsDrawerOpen(false)}
      className="logsDrawer"
    >
      <Box className="logsHeader">
        <Typography variant="h6">
          {selectedResource ? 
            `Logs: ${selectedResource.type === 'pod' ? 'Pod' : 'Deployment'} "${selectedResource.name}"` 
            : 'Logs'}
        </Typography>
        <Box>
          <IconButton onClick={refreshLogs} disabled={loadingLogs}>
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={() => setLogsDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <Divider />
      
      {selectedResource && (
        <Box className="logsInfo">
          <Typography variant="body2">
            <strong>Type:</strong> {selectedResource.type === 'pod' ? 'Pod' : 'Deployment'}
          </Typography>
          <Typography variant="body2">
            <strong>Name:</strong> {selectedResource.name}
          </Typography>
          <Typography variant="body2">
            <strong>Namespace:</strong> {selectedResource.namespace}
          </Typography>
        </Box>
      )}
      
      <Divider />
      
      <Box className="logsContent">
        {loadingLogs ? (
          <Box className="loadingLogs">
            <CircularProgress />
          </Box>
        ) : (
          <pre>
            {logs || 'No logs available'}
          </pre>
        )}
      </Box>
    </Drawer>
  );

  // Function to handle back navigation
  const handleBackClick = () => {
    window.history.back();
  };

  return (
    <Box className="dashboardContainer">
      <a href="#" className="back-button" onClick={(e) => { e.preventDefault(); handleBackClick(); }}>
        &lt; Back
      </a>
      <Box className="header">
        <Box className="title">
          <Box>
            <Typography className="dashboardTitle" component="h1">
              EKS Monitoring Dashboard
            </Typography>
            <hr className="titleDivider" />
            <Box className="clusterInfoBox">
              <span className="clusterLabel">Selected Cluster:</span>
              <span className="clusterName">vaidhyamegha_eks</span>
            </Box>
          </Box>
        </Box>
        <Box className="breadcrumb">
          <a href="/" className="breadcrumb-link">Dashboard</a>
          <span className="breadcrumb-separator">&gt;</span>
          <a href="/monitoring-dashboard" className="breadcrumb-link">Monitoring</a>
          <span className="breadcrumb-separator">&gt;</span>
          <span className="breadcrumb-current">EKS Monitoring Dashboard</span>
        </Box>
      </Box>

      {/* Search bar */}
      <Box className="searchWrapper">
        <Box className="searchContainer">
          <TextField
            className="searchInput"
            placeholder="Search..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} edge="end" size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl variant="outlined" size="small" className="categorySelect">
            <Select
              value={searchCategory}
              onChange={handleSearchCategoryChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Search category' }}
            >
              <MenuItem value="all">Categories</MenuItem>
              <MenuItem value="nodes">Nodes</MenuItem>
              <MenuItem value="pods">Pods</MenuItem>
              <MenuItem value="namespaces">Namespaces</MenuItem>
              <MenuItem value="deployments">Deployments</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {selectedCluster && (
        <>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            className="tabs"
          >
            <Tab label={`Nodes (${searchTerm ? searchResults.nodes.length : nodes.length})`} />
            <Tab label={`Pods (${searchTerm ? searchResults.pods.length : pods.length})`} />
            <Tab label={`Namespaces (${searchTerm ? searchResults.namespaces.length : namespaces.length})`} />
            <Tab label={`Deployments (${searchTerm ? searchResults.deployments.length : deployments.length})`} />
          </Tabs>

          <Box className="resultsCount">
            {renderSearchResultsCount()}
          </Box>

          <TabPanel value={selectedTab} index={0}>
            {renderNodesTable()}
          </TabPanel>
          <TabPanel value={selectedTab} index={1}>
            {renderPodsTable()}
          </TabPanel>
          <TabPanel value={selectedTab} index={2}>
            {renderNamespacesTable()}
          </TabPanel>
          <TabPanel value={selectedTab} index={3}>
            {renderDeploymentsTable()}
          </TabPanel>
          {renderLogsDrawer()}
        </>
      )}
    </Box>
  );
};

export default EksMonitoringDashboard;
